import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Tooltip, Typography } from '@mui/material';
import { getSubtabsFromChecklistResults } from 'app/pages/loanApplication/LoanApplication.utils';
import { generateSuppressedFieldsPath } from 'app/services/loanNavigation';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { LoanApplicationTabName } from 'constants/loanApplication';

import {
  selectLoanApplicationChecklistResultsList,
  selectSuppressedFieldLinksCountByChecklist,
} from 'store/loanApplications/selectors';

import { badgeContainerStyles, badgeLabelStyles } from './SuppressedCountTabBadge.styles';
import { SuppressedCountTabBadgeTestIds } from './SuppressedCountTabBadge.testIds';

/**
 * Amber suppressed-fields count on the Alerts nav tab (DEV-5146): how many
 * field-linked checklist items are currently overridden ("Not needed") across the
 * loan's completeness checklists. Clicking it deep-links to the Suppressed fields
 * section of the first checklist subtab (nav order) that owns a suppression — the
 * Alerts row itself keeps its expand/collapse semantics, so the badge is the click
 * target. Renders nothing when the count is 0 or the new Alerts page is off.
 */
const SuppressedCountTabBadge = ({ tab }) => {
  const { applicationId } = useParams();
  const history = useHistory();

  const isNewAlertsPage = useFeatureToggle(FeatureFlag.ShowNewAlertsPage);

  const countByChecklist = useSelector(selectSuppressedFieldLinksCountByChecklist());
  const checklistResults = useSelector(selectLoanApplicationChecklistResultsList);

  const totalCount = useMemo(
    () => Object.values(countByChecklist).reduce((sum, count) => sum + count, 0),
    [countByChecklist],
  );

  // Same builder the Alerts subtabs render from, so "first subtab with
  // suppressions" matches what the user sees in the nav. Hidden completeness
  // checklists no longer render as subtabs (DEV-5692), yet they are where
  // suppressions live — fall back to the most-suppressed checklist so the deep
  // link (?view=suppressed) keeps working and targets deterministically.
  const targetChecklistId = useMemo(
    () =>
      Object.keys(getSubtabsFromChecklistResults(checklistResults)).find(
        checklistId => countByChecklist[checklistId],
      ) ?? _.maxBy(Object.keys(countByChecklist), checklistId => countByChecklist[checklistId]),
    [checklistResults, countByChecklist],
  );

  const onClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();

      history.push(generateSuppressedFieldsPath({ applicationId, checklistId: targetChecklistId }));
    },
    [history, applicationId, targetChecklistId],
  );

  if (
    tab !== LoanApplicationTabName.ALERTS ||
    !isNewAlertsPage ||
    !applicationId ||
    !totalCount ||
    !targetChecklistId
  ) {
    return null;
  }

  const tooltip = `${totalCount} field${
    totalCount === 1 ? '' : 's'
  } marked not needed — review in Suppressed fields`;

  return (
    <Tooltip title={tooltip}>
      <Box
        aria-label={tooltip}
        component="button"
        data-testid={SuppressedCountTabBadgeTestIds.BADGE_BUTTON}
        sx={badgeContainerStyles}
        type="button"
        onClick={onClick}
      >
        <Typography sx={badgeLabelStyles} variant="bold12">
          {totalCount}
        </Typography>
      </Box>
    </Tooltip>
  );
};

SuppressedCountTabBadge.propTypes = {
  tab: PropTypes.string.isRequired,
};

export default memo(SuppressedCountTabBadge);
